/**
 * lib/partyJournals.ts
 * ------------------------------------------------------------------
 * DEBTOR AND CREDITOR JOURNALS — adjusting an account without inventing a
 * document.
 *
 * Pastel staff use these every day, and the reason is mundane: sometimes an
 * account is simply wrong. A receipt went to the wrong pupil. A balance was
 * taken on twice. Interest is being charged on an overdue account. A supplier
 * allowed a settlement discount. None of those is an invoice, a credit note or a
 * payment, and forcing a clerk to fabricate one to fix an account is how ledgers
 * fill up with documents that describe nothing that ever happened.
 *
 * WHY THIS IS NOT THE SAME AS A CREDIT NOTE. A credit note says "we billed too
 * much and here is the paperwork the parent can see". A debtor journal says "the
 * account is wrong". The first is a document the school stands behind; the
 * second is a correction. Merging them would mean either sending parents
 * paperwork for internal corrections, or losing the audit trail on the
 * corrections — see lib/adjustments.ts for the other half of this pair.
 *
 * WHAT IT WILL NOT DO. It will not move money. A journal here debits or credits
 * a party's control account against some other ledger account; the bank is never
 * one side of it. Money moving is a receipt or a payment, and both already have
 * screens that number, receipt and reconcile properly.
 *
 * PURE. No Firestore, no React.
 * ------------------------------------------------------------------ */
import type { JournalLine } from '@/lib/gl';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export const PARTY_JOURNAL_COLLECTION = 'financePartyJournals';

export type PartySide = 'debtor' | 'creditor';

/**
 * `charge` increases what the party owes or is owed; `credit` decreases it.
 * Named from the party's account outwards rather than as debit/credit, because
 * the whole point is that a clerk should not have to work out which side
 * receivables sits on.
 */
export type PartyDirection = 'charge' | 'credit';

export const PARTY_DIRECTION_LABEL: Record<PartySide, Record<PartyDirection, string>> = {
  debtor: {
    charge: 'Charge the pupil more',
    credit: 'Reduce what the pupil owes',
  },
  creditor: {
    charge: 'Increase what we owe',
    credit: 'Reduce what we owe',
  },
};

/** The reasons that actually come up, so the narration is never just "adjustment". */
export const PARTY_REASONS: Record<PartySide, string[]> = {
  debtor: [
    'Interest charged on an overdue account',
    'Receipt allocated to the wrong pupil',
    'Balance taken on twice',
    'Returned / dishonoured payment',
    'Correction of a keying error',
  ],
  creditor: [
    'Settlement discount allowed by the supplier',
    'Goods returned to the supplier',
    'Invoice captured twice',
    'Payment allocated to the wrong supplier',
    'Correction of a keying error',
  ],
};

export interface PartyJournal {
  id: string;
  journalRef: string;
  side: PartySide;
  direction: PartyDirection;
  /** Pupil id for a debtor, supplier name for a creditor. */
  partyId: string;
  partyName: string;
  /** The other side of the entry — never a bank account. */
  contraAccountCode: string;
  amount: number;
  reason: string;
  date: string;
  fy: string;
  journalId?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  reversedBy?: string;
  reversesId?: string;
}

export const partyCounterId = (year: string): string => `partyJournals-${year}`;
export const formatPartyRef = (side: PartySide, seq: number, year: string): string =>
  `${side === 'debtor' ? 'DJ' : 'CJ'}/${year}/${String(seq).padStart(4, '0')}`;

export interface PartyAccounts {
  /** AR_FEES for a debtor, AP_TRADE for a creditor. */
  control: string;
  contra: string;
}

/**
 * The two lines.
 *
 * The control account moves in the direction the clerk chose; the contra takes
 * the other side. Which is a debit and which a credit falls out of the side and
 * the direction together, which is exactly the arithmetic a clerk should not
 * have to do in their head:
 *
 *   debtor  · charge  Dr receivables  Cr the contra   (pupil owes more)
 *   debtor  · credit  Dr the contra   Cr receivables  (pupil owes less)
 *   creditor· charge  Dr the contra   Cr payables     (we owe more)
 *   creditor· credit  Dr payables     Cr the contra   (we owe less)
 */
export function partyJournalLines(
  j: Pick<PartyJournal, 'side' | 'direction' | 'amount' | 'partyName' | 'reason' | 'journalRef'>,
  accounts: PartyAccounts,
): JournalLine[] {
  const amount = round2(j.amount);
  if (!(amount > 0)) {
    throw new Error('Enter an amount greater than nil. The direction decides which way it goes.');
  }
  if (!accounts.control) throw new Error('No control account — set AR_FEES / AP_TRADE in Chart of Accounts.');
  if (!accounts.contra) throw new Error('Choose the account the other side goes to.');
  if (accounts.control === accounts.contra) {
    throw new Error('The control account and the contra account are the same.');
  }

  const controlIsDebit = j.side === 'debtor' ? j.direction === 'charge' : j.direction === 'credit';
  const who = `${j.journalRef} ${j.partyName}`.trim();

  return controlIsDebit
    ? [
      { accountCode: accounts.control, dr: amount, cr: 0, narration: who },
      { accountCode: accounts.contra, dr: 0, cr: amount, narration: j.reason },
    ]
    : [
      { accountCode: accounts.contra, dr: amount, cr: 0, narration: j.reason },
      { accountCode: accounts.control, dr: 0, cr: amount, narration: who },
    ];
}

/**
 * How the party's own balance moves — the number the clerk is actually aiming
 * at, and what the sub-ledger document has to carry so the control keeps tying.
 *
 * Positive means the party owes more (debtor) or is owed more (creditor).
 */
export function partyBalanceEffect(direction: PartyDirection, amount: number): number {
  return direction === 'charge' ? round2(amount) : -round2(amount);
}

/** A bank or cash account on the contra side means somebody is trying to record
 *  a payment here, which would bypass receipting and reconciliation entirely. */
export function contraIsCashLike(
  contraCode: string,
  systemKeyOf: (code: string) => string | undefined,
): boolean {
  const key = systemKeyOf(contraCode);
  return key === 'BANK_MAIN' || key === 'BANK_SECOND' || key === 'CASH_ON_HAND';
}
